"use client";

import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { supabase } from "@/lib/supabase";
import { useLanguage } from "@/lib/useLanguage";
import { ChristmasOfferingImageViewer } from "@/components/restaurant/ChristmasOfferingImageViewer";
import {
  describeOfferingDates,
  formatOfferingPrice,
  parsePublicMenuOfferingForWeb,
  type WebOfferingDetail,
} from "@/lib/christmas/offeringDetail";
import { appendQueryParams } from "@/lib/app-store-urls";
import { useSearchParams } from "next/navigation";
import { captureChristmasWebOfferingOpened } from "@/lib/christmas/webChristmasAnalytics";

type Props = {
  offeringId: string;
  initialOffering: WebOfferingDetail;
  restaurantName: string;
};

function fill(template: string, vars: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => vars[key] ?? "");
}

export function ChristmasOfferingPageClient({
  offeringId,
  initialOffering,
  restaurantName,
}: Props) {
  const { t } = useLanguage();
  const rs = t.restaurantSharePage;
  const searchParams = useSearchParams();
  const [offering, setOffering] = useState(initialOffering);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  useEffect(() => {
    captureChristmasWebOfferingOpened({
      offering_id: offeringId,
      restaurant_id: initialOffering.restaurantId,
      entry_point: "direct_link",
    });
  }, [offeringId, initialOffering.restaurantId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc("get_public_menu_offering", {
        p_offering_id: offeringId,
      });
      if (cancelled || error || data == null) return;
      const parsed = parsePublicMenuOfferingForWeb(
        data,
        rs.christmasUntitled ?? "Menú de Navidad"
      );
      if (parsed) setOffering(parsed);
    })();
    return () => {
      cancelled = true;
    };
  }, [offeringId, rs.christmasUntitled]);

  const price = formatOfferingPrice(offering.priceEur, offering.currency);
  const dates = describeOfferingDates(offering);
  const availKey = offering.derivedAvailability ?? "";
  const availLabel =
    availKey && rs.availability?.[availKey as keyof typeof rs.availability]
      ? rs.availability[availKey as keyof typeof rs.availability]
      : null;

  const dateLine = useMemo(() => {
    if (dates.kind === "range") {
      return fill(rs.offeringDatesRange ?? "Fechas confirmadas: {from} – {to}", {
        from: dates.from,
        to: dates.to,
      });
    }
    if (dates.kind === "from") {
      return fill(rs.offeringDatesFrom ?? "Desde {from}", { from: dates.from });
    }
    if (dates.kind === "until") {
      return fill(rs.offeringDatesUntil ?? "Hasta {to}", { to: dates.to });
    }
    if (offering.seasonLabel) {
      return fill(rs.offeringSeasonLabel ?? "Temporada: {label}", {
        label: offering.seasonLabel,
      });
    }
    return null;
  }, [dates, offering.seasonLabel, rs]);

  const title =
    offering.packageTitle?.trim() ||
    rs.christmasUntitled ||
    "Paquete de Navidad";
  const restaurantHref = appendQueryParams(
    `/restaurant/${offering.restaurantId}?category=christmas&offering=${encodeURIComponent(offering.id)}`,
    searchParams
  );
  const openInAppHref = appendQueryParams(
    `menuhunter:///offering/${offering.id}`,
    searchParams
  );

  const withdrawn =
    !!offering.archivedAt || offering.derivedAvailability === "archived";

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-gray-900 dark:to-gray-800 pb-10">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-5 mb-4">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            {restaurantName}
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {title}
          </h1>
          {price && (
            <p className="mt-3">
              <span className="inline-block bg-orange-500 text-white text-sm px-2.5 py-0.5 rounded-full font-semibold">
                {price}
              </span>
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">
                {offering.pricePerPerson
                  ? rs.christmasPerPerson
                  : rs.christmasPerPackage}
              </span>
            </p>
          )}
          {availLabel && (
            <p className="mt-2 text-sm font-medium text-blue-600 dark:text-blue-400">
              {availLabel}
            </p>
          )}
          {dateLine && (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              {dateLine}
            </p>
          )}
          {withdrawn && (
            <p className="mt-3 text-sm text-amber-700 dark:text-amber-400">
              {rs.offeringWithdrawn}
            </p>
          )}
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-md p-4 sm:p-5">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            {rs.christmasOpenImages}
          </h2>
          {offering.images.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              {rs.viewerBroken}
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {offering.images.map((img, i) => (
                <button
                  key={img.id ?? `${img.publicUrl}-${i}`}
                  type="button"
                  className="relative aspect-[3/4] rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  onClick={() => {
                    setViewerIndex(i);
                    setViewerOpen(true);
                  }}
                  aria-label={img.alt}
                >
                  <Image
                    src={img.publicUrl}
                    alt={img.alt}
                    fill
                    unoptimized
                    className="object-cover"
                    sizes="(min-width: 640px) 33vw, 50vw"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link
            href={restaurantHref}
            className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            {rs.christmasBackToRestaurant}
          </Link>
          <a
            href={openInAppHref}
            className="inline-flex rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            {rs.openInApp}
          </a>
        </div>
      </div>

      <ChristmasOfferingImageViewer
        open={viewerOpen}
        images={offering.images.map((img) => ({
          publicUrl: img.publicUrl,
          alt: img.alt,
        }))}
        initialIndex={viewerIndex}
        onClose={() => setViewerOpen(false)}
        labels={{
          close: rs.viewerClose,
          previous: rs.viewerPrevious,
          next: rs.viewerNext,
          zoomIn: rs.viewerZoomIn,
          zoomOut: rs.viewerZoomOut,
          broken: rs.viewerBroken,
          loading: rs.viewerLoading,
          of: rs.viewerOf,
        }}
      />
    </div>
  );
}
